// src/yomiri/tracking/DelayedTrackingStore.ts
import type { DelayedTrackingItem, TrackRecord } from './types.ts';

const DB_NAME = 'yomiri_tracking_db';
const DB_VERSION = 1;
const QUEUE_STORE = 'delayed_queue';
const TRACKS_STORE = 'track_records';

class DelayedTrackingStore {
    private dbPromise: Promise<IDBDatabase> | null = null;

    private getDb(): Promise<IDBDatabase> {
        if (!this.dbPromise) {
            this.dbPromise = new Promise((resolve, reject) => {
                const req = indexedDB.open(DB_NAME, DB_VERSION);
                req.onupgradeneeded = () => {
                    const db = req.result;
                    if (!db.objectStoreNames.contains(QUEUE_STORE)) {
                        db.createObjectStore(QUEUE_STORE, { keyPath: 'id' });
                    }
                    if (!db.objectStoreNames.contains(TRACKS_STORE)) {
                        const tracksStore = db.createObjectStore(TRACKS_STORE, { keyPath: ['mangaId', 'trackerId'] });
                        tracksStore.createIndex('mangaId', 'mangaId', { unique: false });
                    }
                };
                req.onsuccess = () => resolve(req.result);
                req.onerror = () => {
                    this.dbPromise = null;
                    reject(req.error);
                };
            });
        }
        return this.dbPromise;
    }

    // === Cola de reintentos offline ===

    async enqueue(item: Omit<DelayedTrackingItem, 'id' | 'timestamp' | 'attempts'>): Promise<void> {
        const db = await this.getDb();
        const id = `${item.trackerId}_${item.mangaId}_${item.remoteId}`;
        const queueItem: DelayedTrackingItem = {
            ...item,
            id,
            timestamp: Date.now(),
            attempts: 0
        };

        return new Promise((resolve, reject) => {
            const tx = db.transaction(QUEUE_STORE, 'readwrite');
            tx.objectStore(QUEUE_STORE).put(queueItem);
            tx.oncomplete = () => {
                console.log(`[TrackingQueue] Encolado progreso para manga ${item.mangaId} en ${item.trackerId}: cap ${item.lastChapterRead}`);
                resolve();
            };
            tx.onerror = () => reject(tx.error);
        });
    }

    async getQueue(): Promise<DelayedTrackingItem[]> {
        const db = await this.getDb();
        return new Promise((resolve, reject) => {
            const tx = db.transaction(QUEUE_STORE, 'readonly');
            const req = tx.objectStore(QUEUE_STORE).getAll();
            req.onsuccess = () => resolve(req.result || []);
            req.onerror = () => reject(req.error);
        });
    }

    async dequeue(id: string): Promise<void> {
        const db = await this.getDb();
        return new Promise((resolve, reject) => {
            const tx = db.transaction(QUEUE_STORE, 'readwrite');
            tx.objectStore(QUEUE_STORE).delete(id);
            tx.oncomplete = () => resolve();
            tx.onerror = () => reject(tx.error);
        });
    }

    // === Persistencia de TrackRecords asociados ===

    async saveTrackRecord(record: TrackRecord): Promise<void> {
        const db = await this.getDb();
        return new Promise((resolve, reject) => {
            const tx = db.transaction(TRACKS_STORE, 'readwrite');
            tx.objectStore(TRACKS_STORE).put(record);
            tx.oncomplete = () => {
                window.dispatchEvent(new CustomEvent('yomiri-tracking-changed', { detail: { mangaId: record.mangaId } }));
                resolve();
            };
            tx.onerror = () => reject(tx.error);
        });
    }

    async getTrackRecordsForManga(mangaId: string): Promise<TrackRecord[]> {
        const db = await this.getDb();
        return new Promise((resolve, reject) => {
            const tx = db.transaction(TRACKS_STORE, 'readonly');
            const index = tx.objectStore(TRACKS_STORE).index('mangaId');
            const req = index.getAll(mangaId);
            req.onsuccess = () => resolve(req.result || []);
            req.onerror = () => reject(req.error);
        });
    }

    async deleteTrackRecord(mangaId: string, trackerId: string): Promise<void> {
        const db = await this.getDb();
        return new Promise((resolve, reject) => {
            const tx = db.transaction(TRACKS_STORE, 'readwrite');
            tx.objectStore(TRACKS_STORE).delete([mangaId, trackerId]);
            tx.oncomplete = () => {
                window.dispatchEvent(new CustomEvent('yomiri-tracking-changed', { detail: { mangaId } }));
                resolve();
            };
            tx.onerror = () => reject(tx.error);
        });
    }
}

export const delayedTrackingStore = new DelayedTrackingStore();
